import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import Container from "@saleor/components/Container";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import MallDeliveryTimeHistory from "@saleor/malls/components/MallDeliveryTimeHistory";
import createSortHandlerWithId from "@saleor/malls/sorts/sortHandler";
import { SmopStatusEnum } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { canManageMalls } from "@saleor/utils/handlers/userHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { decimal, maybe } from "../../../misc";
import MallDetailsPage, {
  MallUpdatePageFormData
} from "../../components/MallDetailsPage";
import {
  useMallUpdateMutation,
  useMallUpdateStatusMutation
} from "../../mutations";
import { useMallDetailsQuery } from "../../queries";
import { MallUpdate } from "../../types/MallUpdate";
import { MallUpdateStatus } from "../../types/MallUpdateStatus";
import { mallListPath, mallShippingLogsSortUrl, mallUrl } from "../../urls";

export interface MallDetailsViewProps {
  id: string;
  params: any;
}

const MallDetails: React.FC<MallDetailsViewProps> = ({ id, params }) => {
  const intl = useIntl();
  const notify = useNotifier();
  const navigate = useNavigator();

  const [openHistoryDialog, setOpenHistoryDialog] = React.useState(false);

  const { data, loading, refetch } = useMallDetailsQuery({
    displayLoader: true,
    variables: {
      id
    }
  });

  const handleUpdateSuccess = (data: MallUpdate) => {
    if (data?.mallUpdate?.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Mall updated"
        })
      });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else if (
      data?.mallUpdate?.errors.some(
        e =>
          e.code === "INVALID" &&
          e.message === "Mall with this Slug and Deleted at already exists."
      )
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Mall name already exists"
        })
      });
    } else if (data?.mallUpdate?.errors.some(e => e.code === "REQUIRED")) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Some Fields have null value!"
        })
      });
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Invalid Form!"
        })
      });
    }
  };

  const handleDeleteSuccess = (data: MallUpdateStatus) => {
    if (data?.updateMallStatus?.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Mall deleted"
        })
      });
      setTimeout(() => {
        navigate(mallListPath);
      }, 1000);
    } else if (data?.updateMallStatus?.errors.some(e => e.code === "INVALID")) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Can't Delete a mall with a store"
        })
      });
      closeModal();
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Something went wrong. Please Try again!"
        })
      });
      closeModal();
    }
  };

  const handleDeactivateSuccess = (data: MallUpdateStatus) => {
    if (data?.updateMallStatus?.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Mall Deactivated"
        })
      });
      setTimeout(() => {
        refetch();
        closeModal();
      }, 1000);
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Something went wrong. Please Try again!"
        })
      });
      closeModal();
    }
  };

  const handleSubmit = (formData: MallUpdatePageFormData) => {
    const mallUpdateInput = {
      address: "",
      city: formData.city,
      code: formData.code,
      contactPerson: formData.contactPerson,
      description: "",
      email: formData.email,
      expressDeliveryAvailable: formData.expressDeliveryAvailable,
      expressLongDistanceAvailable: formData.expressLongDistanceAvailable,
      foodAndBevCutoffTime: formData.foodAndBevCutoffTime
        ? formData.foodAndBevCutoffTime
        : "19:00",
      genMerchCutoffTime: formData.genMerchCutoffTime
        ? formData.genMerchCutoffTime
        : "17:00",
      groceryCutoffTime: formData.groceryCutoffTime
        ? formData.groceryCutoffTime
        : "19:00",
      isInMallPickup: formData.isInMallPickup,
      isNationwideDelivery: formData.isNationwideDelivery,
      isServiceable: formData.isServiceable,
      latitude: decimal(formData.latitude),
      longitude: decimal(formData.longitude),
      mallAdmin: "",
      mallClosing: formData.mallClosing,
      mallOpening: formData.mallOpening,
      mobileNo: formData.mobileNo,
      name: formData.name,
      nextDayAvailable: formData.nextDayAvailable,
      provinceCode: formData.provinceCode,
      provinceName: formData.provinceName,
      sameDayAvailable: formData.sameDayAvailable,
      sameDayGmCutoffTime: formData.sameDayGmCutoffTime
        ? formData.sameDayGmCutoffTime
        : "16:00",
      serviceArea: parseFloat(formData.serviceArea).toFixed(3),
      smMarketExternalCode: formData.smMarketExternalCode,
      smMarketExternalId: formData.smMarketExternalId
        ? formData.smMarketExternalId
        : null,
      smMarketExternalName: formData.smMarketExternalName,
      status: formData.isActive ? SmopStatusEnum.ACT : SmopStatusEnum.INA,
      telephoneNo: formData.telephoneNo,
      totalLandArea: parseFloat(formData.totalLandArea).toFixed(3)
    };

    updateMall({
      variables: {
        id,
        input: mallUpdateInput
      }
    });
  };

  const handleError = errors => {
    const [error] = errors?.graphQLErrors;
    if (error?.message) {
      notify({
        status: "error",
        text: error?.message
      });
    }
  };

  const [updateMall, updateMallResult] = useMallUpdateMutation({
    onCompleted: handleUpdateSuccess,
    onError: handleError
  });

  const [deleteMall, deleteMallResult] = useMallUpdateStatusMutation({
    onCompleted: handleDeleteSuccess,
    onError: handleError
  });

  const [deactiveMall, deactiveMallResult] = useMallUpdateStatusMutation({
    onCompleted: handleDeactivateSuccess,
    onError: handleError
  });

  const mallDetails = data?.mall;

  const [openModal, closeModal] = createDialogActionHandlers(
    navigate,
    params => mallUrl(id, params),
    params
  );

  const handleMallDelete = id => {
    deleteMall({
      variables: {
        id,
        status: SmopStatusEnum.DEL
      }
    });
  };

  const handleMallDeactivate = id => {
    deactiveMall({
      variables: {
        id,
        status: SmopStatusEnum.INA
      }
    });
  };

  const breadcrumbDirectory = [
    {
      name: "Malls",
      url: "/malls"
    },
    {
      name: mallDetails?.name || ""
    }
  ];

  return (
    <>
      <Container>
        <PageBreadcrumbNavigation items={breadcrumbDirectory} />
        <div
          style={{ display: "flex", flexDirection: "row", marginTop: "10px" }}
        >
          <PageBackNavigation onBack={() => navigate(mallListPath)} />
          <PageHeader title={mallDetails?.name || ""}></PageHeader>
        </div>

        <MallDetailsPage
          errors={updateMallResult?.data?.mallUpdate?.errors || []}
          onBack={() => navigate(mallListPath)}
          disabled={
            loading || deleteMallResult.loading || updateMallResult.loading
          }
          saveButtonBarState={updateMallResult.status}
          onSubmit={handleSubmit}
          mallDetails={mallDetails}
          onDelete={() => openModal("delete")}
          onDeactivate={() => openModal("deactivate")}
          onRefetchMall={() => refetch()}
          setOpenHistoryDialog={setOpenHistoryDialog}
          hasManageMallPermission={canManageMalls()}
        />

        <ActionDialog
          confirmButtonState={deleteMallResult.status}
          onClose={closeModal}
          onConfirm={() => handleMallDelete(id)}
          open={params.action === "delete"}
          title={intl.formatMessage({
            defaultMessage: "Delete Mall",
            description: "dialog title"
          })}
          variant="delete"
        >
          <DialogContentText>
            <FormattedMessage
              defaultMessage="Are you sure you want to delete {mallName}?"
              values={{
                mallName: (
                  <strong>{maybe(() => mallDetails.name, "...")}</strong>
                )
              }}
            />
          </DialogContentText>
        </ActionDialog>

        <ActionDialog
          confirmButtonState={deactiveMallResult.status}
          onClose={closeModal}
          onConfirm={() => handleMallDeactivate(id)}
          open={params.action === "deactivate"}
          title={intl.formatMessage({
            defaultMessage: "Deactivate Mall ",
            description: "dialog title"
          })}
          variant="delete"
          confirmButtonLabel="YES"
        >
          <DialogContentText>
            <FormattedMessage defaultMessage="Are you sure to deactive this mall?" />
          </DialogContentText>
        </ActionDialog>
      </Container>

      <MallDeliveryTimeHistory
        params={params}
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        mallId={mallDetails?.id}
        sort={getSortParams(params)}
        onSort={createSortHandlerWithId(
          id,
          navigate,
          mallShippingLogsSortUrl,
          params
        )}
      />
    </>
  );
};

export default MallDetails;
